import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { isAdminHost, isClientHost } from "./portal";

const AuthCtx = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_TOKEN = "instaintel_token";
const STORAGE_WORKSPACE = "instaintel_workspace_org";

// One-time global axios setup: attach Bearer + optional workspace override,
// and blow away the session on 401 so a stale token never renders "empty" UI.
axios.defaults.baseURL = API;
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(STORAGE_TOKEN);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const ws = localStorage.getItem(STORAGE_WORKSPACE);
  if (ws) config.headers["X-Workspace-Org"] = ws;
  return config;
});
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && localStorage.getItem(STORAGE_TOKEN)) {
      localStorage.removeItem(STORAGE_TOKEN);
      localStorage.removeItem(STORAGE_WORKSPACE);
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workspaceOrg, setWorkspaceOrgState] = useState(
    () => localStorage.getItem(STORAGE_WORKSPACE) || ""
  );

  const refresh = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_TOKEN);
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data.user);
    } catch {
      setUser(null);
      localStorage.removeItem(STORAGE_TOKEN);
      localStorage.removeItem(STORAGE_WORKSPACE);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    // Hostname enforcement: block role mismatch at the login boundary so
    // client_user can't reach the admin subdomain and vice versa.
    if (isAdminHost && data.user.role !== "super_admin") {
      throw { response: { data: { detail: "This is the admin portal. Sign in at app.kyros.social instead." } } };
    }
    if (isClientHost && data.user.role === "super_admin") {
      throw { response: { data: { detail: "This is the client portal. Sign in at admin.kyros.social instead." } } };
    }
    localStorage.setItem(STORAGE_TOKEN, data.token);
    localStorage.removeItem(STORAGE_WORKSPACE);
    setWorkspaceOrgState("");
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_WORKSPACE);
    setUser(null);
    setWorkspaceOrgState("");
    window.location.href = "/login";
  };

  const setWorkspaceOrg = (orgId) => {
    if (orgId) localStorage.setItem(STORAGE_WORKSPACE, orgId);
    else localStorage.removeItem(STORAGE_WORKSPACE);
    setWorkspaceOrgState(orgId || "");
    // Force a soft reload of the current page so all queries re-run with the
    // new workspace header.
    window.location.reload();
  };

  const value = useMemo(
    () => ({ user, loading, login, logout, refresh, workspaceOrg, setWorkspaceOrg,
             isAdmin: user?.role === "super_admin" }),
    [user, loading, workspaceOrg, refresh]
  );
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
